import { useCallback } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { URLS, queryClient } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { RootStackParamList } from '@/navigation/types';
import { ripsKeys, tradingCardsFetch, useTradingCardsClient } from '@/providers/TradingCardsClientProvider';
import { BetChannel, getChannel } from '@/utils/bet-channel';
import { logger } from '@/utils/logging';
import { useQuery } from '@tanstack/react-query';

const LOG_TAG = '[useRipsSession]';

// Sentinel URLs handed to Repackz at session creation. They are never loaded: the WebView
// intercepts them and routes the player to the matching native destination instead.
export const RIPS_LOBBY_URL = 'https://betr.app/betr-rips-lobby';
export const RIPS_DEPOSIT_URL = 'https://betr.app/betr-rips-deposit';

/** Opens the user's packs in the Repackz web app. */
const MY_PACKS_PATH = '/my-packs';

/**
 * Leaving a URL out hides its button in the Repackz web app.
 */
export type InitiateSessionRequest = {
    /** Target of the "Back to lobby" button on the Repackz play page. */
    lobbyUrl?: string;
    /** Target of the "Add funds" button shown when the balance is short. */
    depositUrl?: string;
    context: {
        currency: string;
    };
    template: 'modern' | 'clasic';
    /** Channel the session was opened from, stored on the session token and set on the PAM bets. */
    channel: BetChannel;
};

type InitiateSessionResponse = {
    launchUrl: string;
    sessionToken: string;
    currency: string;
    /** ISO 8601 date time */
    expiresAt: string;
};

export type RipsSessionState = 'loading' | 'error' | 'ready';

// Keyed by user, so a new login never reuses the previous user's session.
const currentUserSessionKey = () => ripsKeys.initiateSession(useUserStore.getState().profile.sub);

const SESSION_REQUEST: InitiateSessionRequest = {
    lobbyUrl: RIPS_LOBBY_URL,
    depositUrl: RIPS_DEPOSIT_URL,
    context: { currency: 'USD' },
    template: 'modern',
    channel: getChannel(),
};

type RipsSessionUrlOptions = RootStackParamList['RipsGameWebView'];

/**
 * The backend returns only the query string (e.g. '?session_token=...'), so it needs to be appended
 * to the Repackz web app base URL. Extra path segments go before the query string, extra params
 * after the ones the session already carries.
 */
const buildSessionUrl = (launchUrl: string, { path = '', param }: RipsSessionUrlOptions = {}) => {
    const [launchPath = '', search = ''] = launchUrl.split('?');
    const query = [search, param].filter(Boolean).join('&');

    return `${URLS.REPACKZ_IFRAME_BASE_URL}${launchPath}${path}${query ? `?${query}` : ''}`;
};

const useRipsSessionQuery = () => {
    const tradingCardsClient = useTradingCardsClient();
    const userId = useUserStore(state => state.profile.sub);

    return useQuery<InitiateSessionResponse | null>({
        queryKey: ripsKeys.initiateSession(userId),
        queryFn: () =>
            tradingCardsClient.fetch('/initiate-session', {
                method: 'POST',
                body: JSON.stringify(SESSION_REQUEST),
            }),
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

/**
 * Creates the Repackz session and hands back the navigation entry points into the Rips web app.
 * The session is cached for the whole app session, so mounting this hook on the lobby warms it up
 * and every card tap reuses it.
 */
export const useRipsSession = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const { data: session, isPending, isFetching, isError } = useRipsSessionQuery();

    const launchUrl = session?.launchUrl;

    const openWebView = useCallback(
        (options: RipsSessionUrlOptions) => {
            if (!launchUrl) {
                logger.warn(LOG_TAG, 'No session available, cannot open the Rips web app');

                return;
            }
            navigation.navigate('RipsGameWebView', options);
        },
        [launchUrl, navigation]
    );

    const goToGame = useCallback(
        (gameId: string) => openWebView({ param: `game_id=${encodeURIComponent(gameId)}` }),
        [openWebView]
    );

    const goToVault = useCallback(() => openWebView({ path: MY_PACKS_PATH }), [openWebView]);

    const hasSession = !isError && Boolean(launchUrl);
    const state: RipsSessionState = isPending || isFetching ? 'loading' : hasSession ? 'ready' : 'error';

    return { state, sessionToken: session?.sessionToken, goToGame, goToVault };
};

export const useRipsSessionUrl = (options: RipsSessionUrlOptions) => {
    const { data: session } = useRipsSessionQuery();
    const launchUrl = session?.launchUrl;

    return launchUrl ? buildSessionUrl(launchUrl, options) : undefined;
};

// Only marks the session as stale. The lobby refetches it when it's focused again.
export const expireRipsSession = () =>
    queryClient.invalidateQueries({ queryKey: currentUserSessionKey(), refetchType: 'none' });

export const hasRipsSessionFailed = () => queryClient.getQueryState(currentUserSessionKey())?.status === 'error';

const hasPassedExpiresAt = (session: InitiateSessionResponse | null | undefined) => {
    const expiresAt = Date.parse(session?.expiresAt ?? '');

    return !Number.isNaN(expiresAt) && expiresAt <= Date.now();
};

// `stale: true` skips the call for a loaded session, since staleTime: Infinity means
// it only goes stale once expireRipsSession is called or if it never loaded.
// `expiresAt` is checked here too, for an edge case
// (rips lobby remains mounted and meanwhile the repackz session expires)
export const refreshExpiredRipsSession = () => {
    const session = queryClient.getQueryData<InitiateSessionResponse | null>(currentUserSessionKey());

    if (session === null || hasPassedExpiresAt(session)) {
        expireRipsSession();
    }

    return queryClient.refetchQueries({ queryKey: currentUserSessionKey(), stale: true });
};

/**
 * Deletes the Repackz session and clears the Rips cache. Called on logout.
 */
export const endRipsSession = async () => {
    const sessionToken = queryClient.getQueryData<InitiateSessionResponse | null>(
        currentUserSessionKey()
    )?.sessionToken;

    if (sessionToken) {
        try {
            await tradingCardsFetch(`/sessions/${sessionToken}`, {
                method: 'DELETE',
            });
        } catch (error) {
            logger.warn(LOG_TAG, 'Failed to delete Rips session', error);
        }
    }

    queryClient.removeQueries({ queryKey: ripsKeys.all });
};
